import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthenticatedHeader from '../../components/ui/AuthenticatedHeader';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ChevronProgressTracker from '../../components/ui/ChevronProgressTracker';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/dateUtils';

import LeadInfoPanel from './components/LeadInfoPanel';
import LeadInfoField from './components/LeadInfoField';

import LeadActionPanel from './components/LeadActionPanel';
import { leadService } from '../../services/leadService';

const LeadDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [leadData, setLeadData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchLeadData = async () => {
      // Wait for auth to load before proceeding
      if (authLoading) {
        return;
      }

      // Check authentication after auth is loaded
      if (!user) {
        setError('Please log in to view lead details');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError('');
        
        console.log('Fetching lead details for ID:', id);
        
        if (!id || id === 'undefined' || id === 'null') {
          console.error('Invalid lead ID received:', id);
          setError('Invalid lead ID. Please return to the dashboard and try again.');
          setIsLoading(false);
          return;
        }
        
        const data = await leadService?.getLeadById(id);
        
        console.log('Lead data fetched successfully:', data);
        
        if (!data) {
          setError('Lead not found');
          return;
        }
        
        setLeadData(data);
      } catch (err) {
        console.error('Error loading lead:', err);
        setError(err?.message || 'Failed to load lead details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeadData();
  }, [id, user, authLoading]);

  const getStatusColor = (status) => {
    const colors = {
      'Submitted': 'primary',
      'Under Review': 'secondary',
      'Qualified': 'success',
      'In Progress': 'warning',
      'Converted': 'success',
      'Closed': 'muted',
      'Rejected': 'error'
    };
    return colors?.[status] || 'muted';
  };

  const handleEdit = () => {
    navigate('/create-lead', { state: { leadData, isEdit: true } });
  };

  const handleDownloadPDF = () => {
    console.log('Downloading PDF for lead:', leadData?.id);
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@clear-hawaii.com?subject=Lead Support Request - ' + leadData?.id;
  };

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AuthenticatedHeader />
        <NavigationBreadcrumbs />
        <main className="main-content pt-20 px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-card rounded-lg p-8 border border-border elevation-1 animate-pulse">
              <div className="h-8 bg-muted rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-muted rounded w-2/3 mb-8"></div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  {[1, 2, 3]?.map((i) => (
                    <div key={i} className="h-48 bg-muted rounded"></div>
                  ))}
                </div>
                <div className="h-64 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <AuthenticatedHeader />
        <NavigationBreadcrumbs />
        <main className="main-content pt-20 px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-destructive/10 border border-destructive rounded-lg p-6">
              <div className="flex items-start gap-4">
                <Icon name="AlertCircle" size={24} color="var(--color-destructive)" />
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Lead Details</h3>
                  <p className="text-muted-foreground mb-4">{error}</p>
                  <Button
                    variant="outline"
                    iconName="ArrowLeft"
                    iconPosition="left"
                    onClick={() => navigate('/partner-dashboard')}
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const statusColor = getStatusColor(leadData?.status);
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedHeader />
      <NavigationBreadcrumbs />
      <main className="main-content pt-20 px-6 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-semibold text-foreground">
                    {leadData?.customerName}
                  </h1>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${statusColor}/10 text-${statusColor}`}>
                    {leadData?.status}
                  </span>
                </div>
                <p className="text-muted-foreground mb-2">
                  {leadData?.propertyType} Property Referral
                </p>
                <p className="text-muted-foreground mb-2">
                  Submitted on {formatDate(leadData?.createdAt)}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="text-muted-foreground font-data">Referral ID: {leadData?.salesforceId}</span>
                  
                </div>
              </div>

              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/partner-dashboard')}
                className="lg:mt-0"
              >
                Back to Dashboard
              </Button>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Icon name="Clock" size={16} />
              <span>Last updated: {formatDate(leadData?.updatedAt)}</span>
            </div>

            {/* Chevron Progress Tracker */}
            <ChevronProgressTracker 
              currentStatus={leadData?.status}
              statusDates={{
                'Contacted': leadData?.createdAt,
                'Pending Design': leadData?.statusHistory?.pendingDesign,
                'Pending Permiting': leadData?.statusHistory?.pendingPermitting,
                'Pending Install': leadData?.statusHistory?.pendingInstall,
                'Pending Inspection': leadData?.statusHistory?.pendingInspection,
                'Complete': leadData?.statusHistory?.complete,
              }}
              showAnimation={true}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <LeadInfoPanel title="Customer Information" icon="User">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <LeadInfoField label="Full Name" value={leadData?.customerName} />
                  <LeadInfoField label="Email Address" value={leadData?.email} />
                  <LeadInfoField label="Phone Number" value={leadData?.phone} className="md:col-span-2" />
                </div>
              </LeadInfoPanel>

              <LeadInfoPanel title="Property Information" icon="Building">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <LeadInfoField label="Property Type" value={leadData?.propertyType} />
                  <LeadInfoField label="Address" value={leadData?.propertyAddress} className="md:col-span-2" />
                  <LeadInfoField label="City" value={leadData?.propertyCity} />
                  <LeadInfoField label="State" value={leadData?.propertyState} />
                  <LeadInfoField label="ZIP Code" value={leadData?.propertyZip} />
                </div>
              </LeadInfoPanel>

              <LeadInfoPanel title="Referral Details" icon="Share2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <LeadInfoField label="Referral Type" value={leadData?.referralType} className="md:col-span-2" />
                </div>
              </LeadInfoPanel>

              {leadData?.referralNotes && (
                <LeadInfoPanel title="Additional Notes" icon="FileText">
                  <p className="text-foreground whitespace-pre-line">{leadData?.referralNotes}</p>
                </LeadInfoPanel>
              )}
            </div>

            <div className="lg:col-span-1">
              <LeadActionPanel
                leadStatus={leadData?.status}
                leadData={leadData}
                onEdit={handleEdit}
                onDownloadPDF={handleDownloadPDF}
                onContactSupport={handleContactSupport}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LeadDetails;